import { useEffect, useMemo, useState } from 'react';
import { Download, FileImage, RefreshCw, Search, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SectionHeader } from '@/components/layout/SectionHeader';
import { usePublicDatasetAll, publicDatasetExportUrl } from '@/hooks/use-public-datasets';

// Published dataset: "IKU Perangkat Daerah (Renstra) Kaltara"
// (see docs/ciku-iku-perangkat-daerah-kaltara-api.md)
const API_KEY = 'd9fe7132b103108bdd594eecc849dadaf2d252e5feb027fed46a3ff96f4c672c';
const YEARS = ['2025', '2026', '2027', '2028', '2029', '2030'] as const;

interface IkuRow {
  perangkat_daerah: string;
  kode_skpd: string;
  fpd_id: string;
  indikator_no: number;
  level: string;
  indikator: string;
  satuan: string;
  penyedia_data: string;
  penanggung_jawab: string;
  deskripsi: string;
  formulasi_gambar_url: string;
  kondisi_awal: string;
  kondisi_awal_num: number | null;
  tahun: string;
  target: string;
  target_num: number | null;
}

interface PivotedIndikator {
  indikator_no: number;
  indikator: string;
  level: string;
  satuan: string;
  penyedia_data: string;
  deskripsi: string;
  formulasi_gambar_url: string;
  kondisi_awal: string;
  targets: Record<string, string>;
}

export default function IKUPerangkatDaerahPage() {
  const { data, isLoading, refetch } = usePublicDatasetAll<IkuRow>(API_KEY);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  // distinct OPDs (name + kode), sorted by kode
  const opds = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      if (!map.has(r.perangkat_daerah)) map.set(r.perangkat_daerah, r.kode_skpd);
    }
    return Array.from(map.entries())
      .map(([nama, kode]) => ({ nama, kode }))
      .sort((a, b) => a.kode.localeCompare(b.kode));
  }, [rows]);

  const [opd, setOpd] = useState('');
  const [level, setLevel] = useState<'all' | 'tujuan' | 'sasaran'>('all');
  const [search, setSearch] = useState('');

  // default to first OPD once data arrives
  useEffect(() => {
    if (!opd && opds.length > 0) setOpd(opds[0].nama);
  }, [opds, opd]);

  const selectedKode = opds.find((o) => o.nama === opd)?.kode ?? '';

  // pivot long -> wide (one row per indikator, columns = kondisi awal + each year)
  const indikators = useMemo<PivotedIndikator[]>(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, PivotedIndikator>();
    for (const r of rows) {
      if (r.perangkat_daerah !== opd) continue;
      if (level !== 'all' && r.level !== level) continue;
      if (q && !r.indikator.toLowerCase().includes(q)) continue;
      const key = `${r.indikator_no}|${r.indikator}`;
      let entry = map.get(key);
      if (!entry) {
        entry = {
          indikator_no: r.indikator_no,
          indikator: r.indikator,
          level: r.level,
          satuan: r.satuan,
          penyedia_data: r.penyedia_data,
          deskripsi: r.deskripsi,
          formulasi_gambar_url: r.formulasi_gambar_url,
          kondisi_awal: r.kondisi_awal,
          targets: {},
        };
        map.set(key, entry);
      }
      entry.targets[r.tahun] = r.target;
    }
    return Array.from(map.values()).sort((a, b) => a.indikator_no - b.indikator_no);
  }, [rows, opd, level, search]);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="IKU Perangkat Daerah (Renstra)"
        subtitle="Indikator Kinerja Utama per perangkat daerah Provinsi Kalimantan Utara, kondisi awal & target tahunan 2025–2030 (sumber: CIKU)."
        metric={`${opds.length} OPD`}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isLoading}
              aria-label="Muat ulang data"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button asChild variant="outline">
              <a href={publicDatasetExportUrl(API_KEY)} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </a>
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <Select value={opd} onValueChange={setOpd}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Pilih Perangkat Daerah..." />
            </SelectTrigger>
            <SelectContent>
              {opds.map((o) => (
                <SelectItem key={o.kode} value={o.nama}>
                  {o.nama}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={(v) => setLevel(v as typeof level)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua level</SelectItem>
              <SelectItem value="tujuan">Tujuan</SelectItem>
              <SelectItem value="sasaran">Sasaran</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari indikator..."
              className="pl-9"
            />
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {indikators.length} indikator
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            {opd || 'Perangkat Daerah'}
          </CardTitle>
          <CardDescription>
            {selectedKode ? `Kode SKPD: ${selectedKode} · ` : ''}
            Target per tahun mengikuti dokumen Renstra. Nilai ditampilkan apa adanya dari sumber.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin opacity-20" />
              <p>Memuat data IKU…</p>
            </div>
          ) : indikators.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Tidak ada indikator untuk filter ini.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">No.</TableHead>
                    <TableHead className="min-w-[280px]">Indikator</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Satuan</TableHead>
                    <TableHead className="text-right">Kondisi Awal</TableHead>
                    {YEARS.map((y) => (
                      <TableHead key={y} className="text-right">
                        {y}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {indikators.map((ind) => {
                    const levelVariant: 'default' | 'secondary' =
                      ind.level === 'tujuan' ? 'default' : 'secondary';
                    return (
                    <TableRow key={`${ind.indikator_no}-${ind.indikator}`}>
                      <TableCell className="text-muted-foreground">{ind.indikator_no}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <span className="font-medium" title={ind.deskripsi || undefined}>
                            {ind.indikator}
                          </span>
                          {ind.formulasi_gambar_url ? (
                            <a
                              href={ind.formulasi_gambar_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                              title="Lihat formula (gambar)"
                            >
                              <FileImage className="h-4 w-4" />
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={levelVariant}>{ind.level}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{ind.satuan || '–'}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {ind.kondisi_awal || '–'}
                      </TableCell>
                      {YEARS.map((y) => (
                        <TableCell key={y} className="text-right tabular-nums">
                          {ind.targets[y] || '–'}
                        </TableCell>
                      ))}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
